import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { PermissionGate } from '@/components/PermissionGate';
import {
  Shield, ArrowLeft, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  Clock, Activity, FileText, Users, TrendingDown, TrendingUp,
  Calendar, Target, Plus, Download, Upload, X, ChevronRight,
  Flame, Eye, Link2, BarChart3, GitBranch, Zap, CircleDot, Save, Pencil, DollarSign, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'insights', label: 'AI Insights', icon: Sparkles },
  { id: 'risks', label: 'Risks', icon: Flame },
  { id: 'assumptions', label: 'Assumptions', icon: Target },
  { id: 'issues', label: 'Issues', icon: AlertTriangle },
  { id: 'dependencies', label: 'Dependencies', icon: GitBranch },
  { id: 'matrix', label: 'Risk Matrix', icon: CircleDot },
  { id: 'actions', label: 'Action Tracker', icon: CheckCircle2 },
  { id: 'crossmodule', label: 'Cross-Module', icon: Link2 },
  { id: 'analytics', label: 'Analytics', icon: Activity },
];

const CAT_COLORS = { RISK: '#EF4444', ASSUMPTION: '#F59E0B', ISSUE: '#3B82F6', DEPENDENCY: '#143A6B' };
const RATING_COLORS = { CRITICAL: '#DC2626', HIGH: '#F97316', MEDIUM: '#F59E0B', LOW: '#22C55E' };
const URGENCY_COLORS = { IMMEDIATE: '#DC2626', URGENT: '#F97316', NORMAL: '#3B82F6', LOW: '#94A3B8' };
const STATUS_COLORS = { IDENTIFIED: '#3B82F6', ASSESSED: '#6366F1', RESPONSE_PLANNED: '#143A6B', MONITORING: '#0EA5E9', MATERIALIZED: '#DC2626', REPORTED: '#3B82F6', ACKNOWLEDGED: '#6366F1', UNDER_INVESTIGATION: '#143A6B', ACTION_PLANNED: '#D97706', IN_RESOLUTION: '#F59E0B', RESOLVED: '#22C55E', CLOSED: '#94A3B8', ESCALATED: '#DC2626', COMMUNICATED: '#6366F1', ACKNOWLEDGED_BY_PROVIDER: '#0EA5E9', TRACKING: '#3B82F6', DELIVERED: '#22C55E', AT_RISK: '#F97316', OVERDUE: '#DC2626', VALIDATED_TRUE: '#22C55E', VALIDATED_FALSE: '#DC2626', PARTIALLY_TRUE: '#F59E0B', CLOSED_MITIGATED: '#94A3B8', CLOSED_MATERIALIZED: '#94A3B8', EXPIRED: '#94A3B8', CLOSED_EXPIRED: '#94A3B8', ACCEPTED: '#0EA5E9', RE_ASSESSED: '#6366F1', CLOSED_VALIDATED: '#94A3B8', ESCALATED_TO_RISK: '#DC2626', CLOSED_ESCALATED: '#94A3B8', CLOSED_DELIVERED: '#94A3B8', CLOSED_LATE: '#94A3B8', CLOSED_WAIVED: '#94A3B8' };

const STATUS_OPTIONS = {
  RISK: ['IDENTIFIED', 'ASSESSED', 'RESPONSE_PLANNED', 'MONITORING', 'MATERIALIZED', 'CLOSED_MATERIALIZED', 'EXPIRED', 'CLOSED_EXPIRED', 'CLOSED_MITIGATED', 'ACCEPTED'],
  ASSUMPTION: ['IDENTIFIED', 'MONITORING', 'VALIDATED_TRUE', 'VALIDATED_FALSE', 'PARTIALLY_TRUE', 'RE_ASSESSED', 'CLOSED_VALIDATED', 'ESCALATED_TO_RISK', 'CLOSED_ESCALATED'],
  ISSUE: ['REPORTED', 'ACKNOWLEDGED', 'UNDER_INVESTIGATION', 'ACTION_PLANNED', 'IN_RESOLUTION', 'RESOLVED', 'CLOSED', 'ESCALATED'],
  DEPENDENCY: ['IDENTIFIED', 'COMMUNICATED', 'ACKNOWLEDGED_BY_PROVIDER', 'TRACKING', 'DELIVERED', 'CLOSED_DELIVERED', 'AT_RISK', 'ESCALATED', 'OVERDUE', 'CLOSED_LATE', 'CLOSED_WAIVED'],
};
const DOMAIN_OPTIONS = ['ENGINEERING', 'PROCUREMENT', 'CONSTRUCTION', 'PRECOMM_COMM', 'HSE', 'ENVIRONMENTAL', 'REGULATORY', 'COMMERCIAL', 'CONTRACTUAL', 'FINANCIAL', 'GEOTECHNICAL', 'WEATHER', 'LABOUR', 'LOGISTICS', 'INTERFACE', 'CLIENT', 'VENDOR', 'SUBCONTRACTOR', 'DESIGN', 'QUALITY', 'LEGAL', 'TECHNOLOGY', 'STAKEHOLDER'];
const RESPONSE_OPTIONS = ['MITIGATE', 'AVOID', 'TRANSFER', 'ACCEPT', 'ESCALATE', 'EXPLOIT', 'ENHANCE'];
const PROXIMITY_OPTIONS = ['IMMINENT', 'NEAR_TERM', 'MID_TERM', 'FAR_TERM'];

function Badge({ text, color }) {
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}>{text?.replace(/_/g, ' ')}</span>;
}

function SummaryCard({ label, value, sub, icon: Icon, color, onClick }) {
  return (
    <div onClick={onClick} className={`rounded-xl p-4 ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`} style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`summary-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${color}15` }}><Icon className="w-4 h-4" style={{ color }} /></div>
      </div>
      <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-[10px] font-medium mt-0.5" style={{ color: 'var(--text-muted)' }}>{label}</div>
      {sub && <div className="text-[10px] mt-1" style={{ color }}>{sub}</div>}
    </div>
  );
}

function DataTable({ columns, data, onRowClick }) {
  if (!data?.length) return <div className="text-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>No data</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr style={{ borderBottom: '1px solid var(--border-color)' }}>
          {columns.map(c => <th key={c.key} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{c.label}</th>)}
        </tr></thead>
        <tbody>{data.map((row, i) => (
          <tr key={i} onClick={() => onRowClick?.(row)} className={onRowClick ? 'cursor-pointer hover:brightness-95' : ''} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--hover-bg)' }}>
            {columns.map(c => <td key={c.key} className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>{c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function formatINR(val) {
  if (!val) return '—';
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN');
}

// ════════════════════════════════════════════════════
//  RISK MATRIX 5×5
// ════════════════════════════════════════════════════
function RiskMatrix({ matrixData, onCellClick, matrixType, onToggle }) {
  const cells = matrixData?.cells || [];
  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.probability}_${c.impact}`] = c; });
  const labels = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
  const ratingColor = (p, i) => {
    const s = p * i;
    if (s >= 20) return '#DC262660';
    if (s >= 12) return '#F9731650';
    if (s >= 6) return '#F59E0B40';
    return '#22C55E30';
  };

  return (
    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="risk-matrix">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Risk Matrix — Probability × Impact</h3>
        <div className="flex gap-1">
          {['inherent', 'residual'].map(t => (
            <button key={t} onClick={() => onToggle?.(t)} className="text-[10px] px-3 py-1 rounded-md font-medium transition-all"
              style={{ backgroundColor: matrixType === t ? 'var(--accent-bg)' : 'transparent', color: matrixType === t ? 'var(--accent-text)' : 'var(--text-muted)' }}
              data-testid={`matrix-toggle-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
      </div>
      <div className="flex">
        <div className="flex flex-col justify-between pr-2 py-1" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
          <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>PROBABILITY</span>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-5 gap-1">
            {[5, 4, 3, 2, 1].map(p => (
              [1, 2, 3, 4, 5].map(imp => {
                const cell = cellMap[`${p}_${imp}`] || { risks: [] };
                const count = cell.risks?.length || 0;
                return (
                  <button key={`${p}_${imp}`} onClick={() => count > 0 && onCellClick?.(cell)}
                    className="aspect-square rounded-lg flex flex-col items-center justify-center text-xs transition-all hover:brightness-90"
                    style={{ backgroundColor: ratingColor(p, imp), border: count > 0 ? '2px solid var(--text-primary)' : '1px solid var(--border-color)', minHeight: 52 }}
                    data-testid={`matrix-cell-${p}-${imp}`}>
                    {count > 0 && <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{count}</span>}
                    {count === 0 && <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{p * imp}</span>}
                  </button>
                );
              })
            ))}
          </div>
          <div className="flex justify-between mt-2 px-1">
            {labels.map(l => <span key={l} className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{l}</span>)}
          </div>
          <div className="text-center mt-1"><span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>IMPACT</span></div>
        </div>
        <div className="flex flex-col justify-between pl-2">
          {['V.High', 'High', 'Med', 'Low', 'V.Low'].map(l => <span key={l} className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{l}</span>)}
        </div>
      </div>
      <div className="flex gap-3 mt-3 justify-center">
        {[['LOW', '1-5'], ['MEDIUM', '6-11'], ['HIGH', '12-19'], ['CRITICAL', '20-25']].map(([r, range]) => (
          <span key={r} className="flex items-center gap-1 text-[9px]">
            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: RATING_COLORS[r] }} />
            <span style={{ color: 'var(--text-muted)' }}>{r} ({range})</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
//  CREATE FORM
// ════════════════════════════════════════════════════
function CreateRAIDForm({ projectId, onCreated, onClose }) {
  const [saving, setSaving] = useState(false);
  const [cat, setCat] = useState('RISK');
  const [form, setForm] = useState({ title: '', description: '', raidCategory: 'ENGINEERING', priority: 'MEDIUM', ownerName: '', area: '' });
  const [riskFields, setRiskFields] = useState({ probabilityScore: 3, impactScore: 3, riskType: 'THREAT', responseStrategy: 'MITIGATE', proximity: 'NEAR_TERM' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error('Title required');
    setSaving(true);
    try {
      const body = { ...form, category: cat };
      if (cat === 'RISK') body.riskSpecific = riskFields;
      if (cat === 'ASSUMPTION') body.assumptionSpecific = { assumptionStatement: form.description, confidenceLevel: form.priority === 'HIGH' ? 'LOW' : form.priority === 'LOW' ? 'HIGH' : 'MEDIUM', assumptionSource: 'ESTIMATE_BASIS' };
      if (cat === 'ISSUE') body.issueSpecific = { issueType: form.raidCategory, urgency: form.priority === 'CRITICAL' ? 'IMMEDIATE' : form.priority === 'HIGH' ? 'URGENT' : 'NORMAL' };
      if (cat === 'DEPENDENCY') body.dependencySpecific = { dependencyType: 'EXTERNAL', dependencyDirection: 'INBOUND', provider: { partyName: '', partyType: 'CLIENT' }, deliverable: form.description };
      await api.post(`/v1/projects/${projectId}/raid/items`, body);
      toast.success(`${cat} created`);
      onCreated?.();
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="raid-create-form">
        <div className="sticky top-0 z-10 flex items-center justify-between p-4" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Create RAID Item</h2>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div className="flex gap-1">{['RISK', 'ASSUMPTION', 'ISSUE', 'DEPENDENCY'].map(c => (
            <button key={c} type="button" onClick={() => setCat(c)} className="flex-1 text-xs py-2 rounded-lg font-medium transition-all"
              style={{ backgroundColor: cat === c ? `${CAT_COLORS[c]}20` : 'transparent', color: cat === c ? CAT_COLORS[c] : 'var(--text-muted)', border: `1px solid ${cat === c ? CAT_COLORS[c] : 'var(--border-color)'}` }}
              data-testid={`cat-btn-${c}`}>{c}</button>
          ))}</div>
          <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Title *" className="w-full rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} />
          <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description" rows={2} className="w-full rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} />
          <div className="grid grid-cols-3 gap-3">
            <select value={form.raidCategory} onChange={e => set('raidCategory', e.target.value)} className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
              {['ENGINEERING', 'PROCUREMENT', 'CONSTRUCTION', 'HSE', 'COMMERCIAL', 'REGULATORY', 'FINANCIAL', 'DESIGN', 'QUALITY', 'CLIENT', 'VENDOR', 'LABOUR', 'WEATHER', 'GEOTECHNICAL', 'LOGISTICS'].map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
            <select value={form.priority} onChange={e => set('priority', e.target.value)} className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} placeholder="Owner Name" className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          {cat === 'RISK' && (
            <div className="grid grid-cols-3 gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)' }}>
              <div><label className="text-[10px] block mb-1" style={{ color: 'var(--text-muted)' }}>Probability (1-5)</label>
                <select value={riskFields.probabilityScore} onChange={e => setRiskFields(p => ({ ...p, probabilityScore: +e.target.value }))} className="w-full rounded px-2 py-1 text-xs" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} — {['V.Low', 'Low', 'Medium', 'High', 'V.High'][v - 1]}</option>)}
                </select></div>
              <div><label className="text-[10px] block mb-1" style={{ color: 'var(--text-muted)' }}>Impact (1-5)</label>
                <select value={riskFields.impactScore} onChange={e => setRiskFields(p => ({ ...p, impactScore: +e.target.value }))} className="w-full rounded px-2 py-1 text-xs" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} — {['V.Low', 'Low', 'Medium', 'High', 'V.High'][v - 1]}</option>)}
                </select></div>
              <div><label className="text-[10px] block mb-1" style={{ color: 'var(--text-muted)' }}>Response</label>
                <select value={riskFields.responseStrategy} onChange={e => setRiskFields(p => ({ ...p, responseStrategy: e.target.value }))} className="w-full rounded px-2 py-1 text-xs" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  {['MITIGATE', 'AVOID', 'TRANSFER', 'ACCEPT', 'ESCALATE', 'EXPLOIT', 'ENHANCE'].map(v => <option key={v} value={v}>{v}</option>)}
                </select></div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="raid-submit-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Create {cat}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
//  DETAIL SLIDE-OUT (EDITABLE)
// ════════════════════════════════════════════════════
function CrossModuleLinkModal({ projectId, items, onClose, onLinked }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    raidNumber: '', module: 'HSE', referenceId: '', referenceLabel: '',
    linkType: 'IMPACTS', impactDescription: '', contingencyAmount: '', contingencyDrawn: '',
  });
  const [hseIncidents, setHseIncidents] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputCls = "w-full rounded-lg px-3 py-2 text-sm";
  const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };
  const labelCls = "text-xs font-medium mb-1 block";
  const labelStyle = { color: 'var(--text-muted)' };

  useEffect(() => {
    api.get(`/v1/projects/${projectId}/hse/incidents?limit=50`).then(r => setHseIncidents(r.data?.incidents || [])).catch(() => {});
  }, [projectId]);

  const handleSubmit = async () => {
    if (!form.raidNumber || !form.referenceId) { toast.error('RAID item and reference are required'); return; }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/raid/items/${form.raidNumber}/cross-link`, {
        module: form.module, referenceId: form.referenceId, referenceLabel: form.referenceLabel,
        linkType: form.linkType, impactDescription: form.impactDescription,
        contingencyAmount: +form.contingencyAmount || 0, contingencyDrawn: +form.contingencyDrawn || 0,
      });
      toast.success('Cross-module link created');
      onLinked();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const linkTypes = {
    HSE: ['IMPACTS', 'CAUSED_BY', 'RESULTED_IN', 'MITIGATES'],
    SCHEDULE: ['DELAYS', 'IMPACTS', 'DEPENDS_ON', 'BLOCKS'],
    COST: ['CONTINGENCY', 'IMPACTS', 'OVERRUN'],
    MANPOWER: ['IMPACTS', 'DEPENDS_ON'],
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="cross-link-modal">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Add Cross-Module Link</h2>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>RAID Item *</span>
              <select value={form.raidNumber} onChange={e => set('raidNumber', e.target.value)} className={inputCls} style={inputStyle} data-testid="link-raid-select">
                <option value="">— Select —</option>
                {items.map(i => <option key={i.raidNumber} value={i.raidNumber}>{i.raidNumber} — {i.title?.slice(0, 30)}</option>)}
              </select>
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Target Module *</span>
              <select value={form.module} onChange={e => set('module', e.target.value)} className={inputCls} style={inputStyle} data-testid="link-module-select">
                {['HSE', 'SCHEDULE', 'COST', 'MANPOWER'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>Reference ID *</span>
              {form.module === 'HSE' && hseIncidents.length > 0 ? (
                <select value={form.referenceId} onChange={e => { set('referenceId', e.target.value); set('referenceLabel', `Incident ${e.target.value}`); }} className={inputCls} style={inputStyle} data-testid="link-ref-select">
                  <option value="">— Select Incident —</option>
                  {hseIncidents.map(i => <option key={i.incidentNumber} value={i.incidentNumber}>{i.incidentNumber} ({i.incidentType})</option>)}
                </select>
              ) : (
                <input value={form.referenceId} onChange={e => set('referenceId', e.target.value)} className={inputCls} style={inputStyle} placeholder={form.module === 'SCHEDULE' ? 'e.g. A1010' : form.module === 'COST' ? 'e.g. CC-PIP-001' : 'Reference ID'} data-testid="link-ref-input" />
              )}
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Link Type</span>
              <select value={form.linkType} onChange={e => set('linkType', e.target.value)} className={inputCls} style={inputStyle} data-testid="link-type-select">
                {(linkTypes[form.module] || ['IMPACTS']).map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          {form.module === 'COST' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className={labelCls} style={labelStyle}>Contingency Amount</span>
                <input type="number" value={form.contingencyAmount} onChange={e => set('contingencyAmount', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="link-contingency-input" />
              </div>
              <div>
                <span className={labelCls} style={labelStyle}>Contingency Drawn</span>
                <input type="number" value={form.contingencyDrawn} onChange={e => set('contingencyDrawn', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="link-drawn-input" />
              </div>
            </div>
          )}
          <div>
            <span className={labelCls} style={labelStyle}>Impact Description</span>
            <input value={form.impactDescription} onChange={e => set('impactDescription', e.target.value)} className={inputCls} style={inputStyle} placeholder="Describe the impact..." data-testid="link-desc-input" />
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Reference Label</span>
            <input value={form.referenceLabel} onChange={e => set('referenceLabel', e.target.value)} className={inputCls} style={inputStyle} placeholder="Friendly label" data-testid="link-label-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="link-submit-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />} Create Link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditSelect({ value, options, onChange, testId }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} className="rounded px-2 py-1 text-xs w-full" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid={testId}>
      {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  );
}

function ItemDetail({ item, projectId, onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!item) return;
    const rs = item.riskSpecific || {};
    setForm({
      status: item.status || 'IDENTIFIED',
      priority: item.priority || 'MEDIUM',
      raidCategory: item.raidCategory || 'ENGINEERING',
      ownerName: item.ownerName || '',
      targetResolutionDate: item.targetResolutionDate?.slice(0, 10) || '',
      actualResolutionDate: item.actualResolutionDate?.slice(0, 10) || '',
      mitigationPlan: item.mitigationPlan || '',
      description: item.description || '',
      probabilityScore: rs.probabilityScore || 3,
      impactScore: rs.impactScore || 3,
      responseStrategy: rs.responseStrategy || 'MITIGATE',
      proximity: rs.proximity || 'NEAR_TERM',
      residualProbabilityScore: rs.residualProbabilityScore || rs.probabilityScore || 3,
      residualImpactScore: rs.residualImpactScore || rs.impactScore || 3,
    });
    setEditing(false);
  }, [item]);

  // Early return after hooks
  if (!item) return null;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const agingDays = item.identifiedDate ? Math.floor((new Date() - new Date(item.identifiedDate)) / 86400000) : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        description: form.description,
        priority: form.priority,
        raidCategory: form.raidCategory,
        ownerName: form.ownerName,
        targetResolutionDate: form.targetResolutionDate ? new Date(form.targetResolutionDate).toISOString() : null,
        actualResolutionDate: form.actualResolutionDate ? new Date(form.actualResolutionDate).toISOString() : null,
        mitigationPlan: form.mitigationPlan,
      };
      if (item.category === 'RISK') {
        body.riskSpecific = {
          ...(item.riskSpecific || {}),
          probabilityScore: +form.probabilityScore,
          impactScore: +form.impactScore,
          responseStrategy: form.responseStrategy,
          proximity: form.proximity,
          residualProbabilityScore: +form.residualProbabilityScore,
          residualImpactScore: +form.residualImpactScore,
        };
      }
      await api.put(`/v1/projects/${projectId}/raid/items/${item.raidNumber}`, body);
      if (form.status !== item.status) {
        await api.put(`/v1/projects/${projectId}/raid/items/${item.raidNumber}/status`, { status: form.status });
      }
      toast.success('Item updated');
      setEditing(false);
      onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const rs = item.riskSpecific || {};
  const as = item.assumptionSpecific || {};
  const is_ = item.issueSpecific || {};
  const ds = item.dependencySpecific || {};
  const inputCls = "w-full rounded px-2 py-1 text-xs";
  const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };
  const labelCls = "text-[10px] font-medium mb-0.5 block";
  const labelStyle = { color: 'var(--text-muted)' };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg z-50 shadow-2xl overflow-y-auto" style={{ backgroundColor: 'var(--card-bg)', borderLeft: '2px solid var(--border-color)' }} data-testid="raid-detail-panel">
      <div className="sticky top-0 z-10 flex items-center justify-between p-4" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <Badge text={item.category} color={CAT_COLORS[item.category]} />
          <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{item.raidNumber}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-mono" style={{ backgroundColor: agingDays > 60 ? '#DC262615' : agingDays > 30 ? '#F97316' + '15' : '#3B82F615', color: agingDays > 60 ? '#DC2626' : agingDays > 30 ? '#F97316' : '#3B82F6', border: `1px solid ${agingDays > 60 ? '#DC262630' : agingDays > 30 ? '#F9731630' : '#3B82F630'}` }} data-testid="detail-aging">{agingDays}d aging</span>
        </div>
        <div className="flex items-center gap-2">
          {!editing && <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:brightness-90 transition-all" style={{ backgroundColor: 'var(--hover-bg)' }} data-testid="detail-edit-btn"><Pencil className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>}
          {editing && <Button size="sm" onClick={handleSave} disabled={saving} data-testid="detail-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save</Button>}
          {editing && <button onClick={() => setEditing(false)} className="text-xs px-2 py-1 rounded" style={{ color: 'var(--text-muted)' }}>Cancel</button>}
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
      </div>
      <div className="p-4 space-y-4">
        <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>

        {/* Description */}
        {editing ? (
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className={inputCls} style={inputStyle} data-testid="detail-description-input" />
        ) : (
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
        )}

        {/* Core Fields Grid */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className={labelCls} style={labelStyle}>Status</span>
            {editing ? <EditSelect value={form.status} options={STATUS_OPTIONS[item.category] || []} onChange={v => set('status', v)} testId="detail-status-select" /> : <Badge text={item.status} color={STATUS_COLORS[item.status] || '#94A3B8'} />}
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Priority</span>
            {editing ? <EditSelect value={form.priority} options={['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']} onChange={v => set('priority', v)} testId="detail-priority-select" /> : <Badge text={item.priority} color={RATING_COLORS[item.priority] || '#94A3B8'} />}
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Domain</span>
            {editing ? <EditSelect value={form.raidCategory} options={DOMAIN_OPTIONS} onChange={v => set('raidCategory', v)} testId="detail-domain-select" /> : <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.raidCategory?.replace(/_/g, ' ')}</span>}
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Owner</span>
            {editing ? <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-owner-input" /> : <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.ownerName}</span>}
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Identified</span>
            <span style={{ color: 'var(--text-primary)' }}>{item.identifiedDate?.slice(0, 10)}</span>
          </div>
          <div>
            <span className={labelCls} style={labelStyle}>Target Closure Date</span>
            {editing ? <input type="date" value={form.targetResolutionDate} onChange={e => set('targetResolutionDate', e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-target-date-input" /> : <span style={{ color: 'var(--text-primary)' }}>{item.targetResolutionDate?.slice(0, 10) || '—'}</span>}
          </div>
          <div className="col-span-2">
            <span className={labelCls} style={labelStyle}>Actual Closure Date</span>
            {editing ? <input type="date" value={form.actualResolutionDate} onChange={e => set('actualResolutionDate', e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-actual-date-input" /> : <span style={{ color: item.actualResolutionDate ? '#22C55E' : 'var(--text-disabled)' }}>{item.actualResolutionDate?.slice(0, 10) || '— Not closed'}</span>}
          </div>
        </div>

        {/* Mitigation Plan */}
        <div>
          <span className={labelCls} style={labelStyle}>Mitigation / Response Plan</span>
          {editing ? (
            <textarea value={form.mitigationPlan} onChange={e => set('mitigationPlan', e.target.value)} rows={3} placeholder="Describe the mitigation strategy, contingency actions, and responsible parties..." className={inputCls} style={inputStyle} data-testid="detail-mitigation-input" />
          ) : (
            <p className="text-xs rounded-lg p-2" style={{ backgroundColor: 'var(--hover-bg)', color: item.mitigationPlan ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{item.mitigationPlan || 'No mitigation plan documented'}</p>
          )}
        </div>

        {/* Risk Assessment */}
        {item.category === 'RISK' && (
          <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--hover-bg)' }}>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Risk Assessment</h4>
            {editing ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className={labelCls} style={labelStyle}>Probability (1-5)</span>
                    <select value={form.probabilityScore} onChange={e => set('probabilityScore', +e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-prob-select">
                      {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} — {['V.Low', 'Low', 'Medium', 'High', 'V.High'][v - 1]}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className={labelCls} style={labelStyle}>Impact (1-5)</span>
                    <select value={form.impactScore} onChange={e => set('impactScore', +e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-impact-select">
                      {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} — {['V.Low', 'Low', 'Medium', 'High', 'V.High'][v - 1]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="text-xs text-center py-1 rounded" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Score: </span>
                  <strong style={{ color: RATING_COLORS[form.probabilityScore * form.impactScore >= 20 ? 'CRITICAL' : form.probabilityScore * form.impactScore >= 12 ? 'HIGH' : form.probabilityScore * form.impactScore >= 6 ? 'MEDIUM' : 'LOW'] }}>{form.probabilityScore * form.impactScore}</strong>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className={labelCls} style={labelStyle}>Response Strategy</span>
                    <EditSelect value={form.responseStrategy} options={RESPONSE_OPTIONS} onChange={v => set('responseStrategy', v)} testId="detail-response-select" />
                  </div>
                  <div>
                    <span className={labelCls} style={labelStyle}>Proximity</span>
                    <EditSelect value={form.proximity} options={PROXIMITY_OPTIONS} onChange={v => set('proximity', v)} testId="detail-proximity-select" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className={labelCls} style={labelStyle}>Residual P (1-5)</span>
                    <select value={form.residualProbabilityScore} onChange={e => set('residualProbabilityScore', +e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-res-prob-select">
                      {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className={labelCls} style={labelStyle}>Residual I (1-5)</span>
                    <select value={form.residualImpactScore} onChange={e => set('residualImpactScore', +e.target.value)} className={inputCls} style={inputStyle} data-testid="detail-res-imp-select">
                      {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>P: <strong style={{ color: RATING_COLORS[rs.riskRating] }}>{rs.probability || ['', 'V.Low', 'Low', 'Medium', 'High', 'V.High'][rs.probabilityScore] || '—'}</strong> ({rs.probabilityScore})</div>
                  <div>I: <strong style={{ color: RATING_COLORS[rs.riskRating] }}>{rs.impact || ['', 'V.Low', 'Low', 'Medium', 'High', 'V.High'][rs.impactScore] || '—'}</strong> ({rs.impactScore})</div>
                  <div>Score: <strong style={{ color: RATING_COLORS[rs.riskRating] }}>{rs.riskScore}</strong> ({rs.riskRating})</div>
                </div>
                {rs.residualRiskScore && <div className="mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>Residual: P{rs.residualProbabilityScore} × I{rs.residualImpactScore} = {rs.residualRiskScore}</div>}
                {rs.responseStrategy && <div className="mt-1 text-xs">Response: <Badge text={rs.responseStrategy} color="#6366F1" /></div>}
                {rs.proximity && <div className="mt-1 text-xs">Proximity: <Badge text={rs.proximity} color="#0EA5E9" /></div>}
                {rs.quantitativeAssessment?.expectedMonetaryValue && <div className="mt-1 text-xs">EMV: <strong style={{ color: '#EF4444' }}>{formatINR(rs.quantitativeAssessment.expectedMonetaryValue)}</strong></div>}
              </>
            )}
          </div>
        )}

        {/* Assumption Details */}
        {item.category === 'ASSUMPTION' && (
          <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--hover-bg)' }}>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Assumption Details</h4>
            {as.assumptionStatement && <p className="text-xs italic mb-1" style={{ color: 'var(--text-secondary)' }}>"{as.assumptionStatement}"</p>}
            <div className="text-xs space-y-1">
              {as.confidenceLevel && <div>Confidence: <Badge text={as.confidenceLevel} color={as.confidenceLevel === 'HIGH' ? '#22C55E' : as.confidenceLevel === 'LOW' ? '#EF4444' : '#F59E0B'} /></div>}
              {as.validationResult && <div>Validation: <Badge text={as.validationResult} color={STATUS_COLORS[as.validationResult] || '#94A3B8'} /></div>}
              {as.impactIfFalse && <div className="text-red-400">If false: {as.impactIfFalse}</div>}
            </div>
          </div>
        )}

        {/* Issue Details */}
        {item.category === 'ISSUE' && (
          <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--hover-bg)' }}>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Issue Details</h4>
            <div className="text-xs space-y-1">
              {is_.urgency && <div>Urgency: <Badge text={is_.urgency} color={URGENCY_COLORS[is_.urgency] || '#94A3B8'} /></div>}
              {is_.escalationLevel > 0 && <div>Escalation: Level <strong>{is_.escalationLevel}</strong> → {is_.escalatedTo}</div>}
              {is_.rootCauseCategory && <div>Root Cause: {is_.rootCauseCategory?.replace(/_/g, ' ')}</div>}
              {is_.originatedFrom && <div>Origin: <Badge text={is_.originatedFrom} color="#6366F1" /></div>}
            </div>
          </div>
        )}

        {/* Dependency Details */}
        {item.category === 'DEPENDENCY' && (
          <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--hover-bg)' }}>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Dependency Details</h4>
            <div className="text-xs space-y-1">
              <div>{ds.dependencyDirection} — {ds.dependencyType}</div>
              {ds.provider?.partyName && <div>Provider: <strong>{ds.provider.partyName}</strong> ({ds.provider.partyType?.replace(/_/g, ' ')})</div>}
              {ds.deliverable && <div>Deliverable: {ds.deliverable}</div>}
              {ds.requiredDate && <div>Required: {ds.requiredDate?.slice(0, 10)}</div>}
              {ds.delayDays != null && <div className={ds.delayDays > 0 ? 'text-red-400' : 'text-green-400'}>Delay: {ds.delayDays} days</div>}
            </div>
          </div>
        )}

        {/* Action Items */}
        {item.actionItems?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Action Items ({item.actionItems.length})</h4>
            {item.actionItems.map((a, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 text-xs" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <Badge text={a.status} color={a.status === 'COMPLETED' ? '#22C55E' : a.status === 'OVERDUE' ? '#EF4444' : '#3B82F6'} />
                <div className="flex-1"><span style={{ color: 'var(--text-primary)' }}>{a.actionDescription}</span><br /><span style={{ color: 'var(--text-muted)' }}>{a.responsiblePerson} — {a.targetDate?.slice(0, 10)}</span></div>
              </div>
            ))}
          </div>
        )}

        {/* Linked Items */}
        {item.linkedItems?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Linked Items ({item.linkedItems.length})</h4>
            {item.linkedItems.map((l, i) => (
              <div key={i} className="flex items-center gap-2 py-1 text-xs">
                <Link2 className="w-3 h-3" style={{ color: CAT_COLORS[l.linkedCategory] }} />
                <Badge text={l.linkedRaidNumber} color={CAT_COLORS[l.linkedCategory] || '#94A3B8'} />
                <span style={{ color: 'var(--text-muted)' }}>{l.linkType?.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}

        {/* Schedule/Cost */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          {item.scheduleImpact?.hasScheduleImpact && <div className="rounded-lg p-2" style={{ backgroundColor: '#F5910B10' }}>
            <Clock className="w-3 h-3 mb-1 text-orange-400" /> Schedule: {item.scheduleImpact.impactDays} days
          </div>}
          {item.costImpact?.hasCostImpact && <div className="rounded-lg p-2" style={{ backgroundColor: '#EF444410' }}>
            <TrendingDown className="w-3 h-3 mb-1 text-red-400" /> Cost: {formatINR(item.costImpact.estimatedCost)}
          </div>}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════
export default function RAIDDashboard() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const projectId = selectedProject?.project_id;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [matrixType, setMatrixType] = useState('inherent');

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [matrixData, setMatrixData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [aging, setAging] = useState([]);
  const [ownerData, setOwnerData] = useState([]);
  const [emvPareto, setEmvPareto] = useState([]);
  const [riskProfile, setRiskProfile] = useState([]);
  const [catDist, setCatDist] = useState(null);
  const [resolutionPerf, setResolutionPerf] = useState(null);
  const [crossLinks, setCrossLinks] = useState(null);
  const [crossModuleSummary, setCrossModuleSummary] = useState(null);
  const [contingencyData, setContingencyData] = useState(null);
  const [hseLinksData, setHseLinksData] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const base = `/v1/projects/${projectId}/raid`;
      const [sumRes, itemsRes, matRes, trendRes, agingRes, ownerRes, emvRes, profRes, catRes, resPerfRes, crossRes, cmSumRes, contRes, hseRes] = await Promise.all([
        api.get(`${base}/summary`).catch(() => ({ data: {} })),
        api.get(`${base}/items?limit=500`).catch(() => ({ data: { items: [] } })),
        api.get(`${base}/risks/matrix?matrix_type=${matrixType}`).catch(() => ({ data: {} })),
        api.get(`${base}/analytics/trend`).catch(() => ({ data: { trend: [] } })),
        api.get(`${base}/analytics/aging`).catch(() => ({ data: { aging: [] } })),
        api.get(`${base}/analytics/owner-workload`).catch(() => ({ data: { owners: [] } })),
        api.get(`${base}/analytics/emv-pareto`).catch(() => ({ data: { pareto: [] } })),
        api.get(`${base}/analytics/risk-profile`).catch(() => ({ data: { profile: [] } })),
        api.get(`${base}/analytics/category-distribution`).catch(() => ({ data: {} })),
        api.get(`${base}/analytics/resolution-performance`).catch(() => ({ data: null })),
        api.get(`${base}/analytics/cross-links`).catch(() => ({ data: { nodes: [], edges: [] } })),
        api.get(`${base}/cross-module/summary`).catch(() => ({ data: null })),
        api.get(`${base}/cross-module/contingency`).catch(() => ({ data: null })),
        api.get(`${base}/cross-module/hse-links`).catch(() => ({ data: null })),
      ]);
      setSummary(sumRes.data);
      setItems(itemsRes.data?.items || []);
      setMatrixData(matRes.data);
      setTrend(trendRes.data?.trend || []);
      setAging(agingRes.data?.aging || []);
      setOwnerData(ownerRes.data?.owners || []);
      setEmvPareto(emvRes.data?.pareto || []);
      setRiskProfile(profRes.data?.profile || []);
      setCatDist(catRes.data);
      setResolutionPerf(resPerfRes.data);
      setCrossLinks(crossRes.data);
      setCrossModuleSummary(cmSumRes.data);
      setContingencyData(contRes.data);
      setHseLinksData(hseRes.data);
    } catch { toast.error('Failed to load RAID data'); }
    finally { setLoading(false); }
  }, [projectId, matrixType]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Deep-link support: ?raid=A-042 → open item drawer
  useEffect(() => {
    if (!items.length) return;
    const params = new URLSearchParams(window.location.search);
    const raidNo = params.get('raid');
    if (!raidNo) return;
    const match = items.find(i => i.raidNumber === raidNo);
    if (match) {
      setSelectedItem(match);
      toast.info(`Opened ${raidNo} from deep-link`);
    }
  }, [items]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/raid/seed`);
      if (res.data?.seeded) { toast.success(`RAID seeded: ${res.data.total} items`); fetchAll(); }
      else toast.info(res.data?.message || 'Data exists');
    } catch { toast.error('Seed failed'); }
    finally { setSeeding(false); }
  };

  const handleExport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v1/projects/${projectId}/raid/export`, '_blank');
  };

  const handleTemplateDownload = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v1/projects/${projectId}/raid/items/import-template`, '_blank');
  };

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/v1/projects/${projectId}/raid/items/bulk-import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      toast.success(`Imported ${d.imported} items${d.errors?.length ? ` (${d.errors.length} errors)` : ''}`);
      if (d.errors?.length) d.errors.slice(0, 3).forEach(e => toast.error(`Row ${e.row} (${e.sheet}): ${e.error}`));
      setShowImport(false);
      fetchAll();
    } catch { toast.error('Import failed'); }
  };

  const filteredItems = useCallback((cat) => items.filter(i => i.category === cat), [items]);
  const allActions = useMemo(() => {
    const acts = [];
    items.forEach(item => {
      (item.actionItems || []).forEach(a => { acts.push({ ...a, parentRaid: item.raidNumber, parentCategory: item.category, parentTitle: item.title, parentPriority: item.priority }); });
    });
    return acts;
  }, [items]);

  const s = summary || {};
  const DONUT_DATA = [
    { name: 'Risks', value: s.risks?.open || 0, color: CAT_COLORS.RISK },
    { name: 'Assumptions', value: s.assumptions?.monitoring || 0, color: CAT_COLORS.ASSUMPTION },
    { name: 'Issues', value: s.issues?.open || 0, color: CAT_COLORS.ISSUE },
    { name: 'Dependencies', value: s.dependencies?.open || 0, color: CAT_COLORS.DEPENDENCY },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="raid-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="raid-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl" style={{ background: 'linear-gradient(135deg, #EF444420, #F59E0B20)', border: '1px solid #EF444430' }}>
                <Shield className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>RAID Log</h1>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Risks · Assumptions · Issues · Dependencies</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="raid" />
            <PermissionGate module="RAID" action="create">
              <Button size="sm" onClick={() => setShowCreate(true)} data-testid="raid-create-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Item</Button>
            </PermissionGate>
            <PermissionGate module="RAID" action="import">
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="raid-import-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Import</Button>
            </PermissionGate>
            <PermissionGate module="RAID" action="export">
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="raid-export-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
            </PermissionGate>
            <PermissionGate module="RAID" action="configure">
              <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="raid-seed-btn">{seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Seed</Button>
            </PermissionGate>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="RAID_LOG" defaultOpen={false} compact={true} />
        )}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1" style={{ borderBottom: '1px solid var(--border-color)' }}>
          {TABS.map(tab => { const Icon = tab.icon; const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-all rounded-t-lg"
              style={{ color: isActive ? 'var(--accent-text)' : 'var(--text-muted)', backgroundColor: isActive ? 'var(--accent-bg)' : 'transparent', borderBottom: isActive ? '2px solid var(--accent-text)' : '2px solid transparent' }}
              data-testid={`raid-tab-${tab.id}`}><Icon className="w-3.5 h-3.5" />{tab.label}</button>;
          })}
        </div>

        {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>}

        {!loading && projectId && (
          <>
            {/* AI INSIGHTS TAB */}
            {activeTab === 'insights' && (
              <ModuleInsightsDashboard projectId={projectId} moduleKey="raid"
                color="#EF4444" deepLinkBase="/project-controls/raid" />
            )}
            {/* DASHBOARD TAB */}
            {activeTab === 'dashboard' && summary && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <SummaryCard label="Open Risks" value={s.risks?.open || 0} sub={`${s.risks?.byRating?.critical || 0} critical · EMV ${formatINR(s.risks?.totalEMV)}`} icon={Flame} color="#EF4444" onClick={() => setActiveTab('risks')} />
                  <SummaryCard label="Assumptions" value={s.assumptions?.monitoring || 0} sub={`${s.assumptions?.overdueValidation || 0} overdue`} icon={Target} color="#F59E0B" onClick={() => setActiveTab('assumptions')} />
                  <SummaryCard label="Open Issues" value={s.issues?.open || 0} sub={`${s.issues?.escalated || 0} escalated`} icon={AlertTriangle} color="#3B82F6" onClick={() => setActiveTab('issues')} />
                  <SummaryCard label="Dependencies" value={s.dependencies?.open || 0} sub={`${s.dependencies?.atRisk || 0} at risk`} icon={GitBranch} color="#143A6B" onClick={() => setActiveTab('dependencies')} />
                  <SummaryCard label="Actions" value={s.crossMetrics?.totalActionItems || 0} sub={`${s.crossMetrics?.actionItemsOpen || 0} open`} icon={CheckCircle2} color="#0EA5E9" onClick={() => setActiveTab('actions')} />
                  <SummaryCard label="Overdue" value={s.crossMetrics?.actionItemsOverdue || 0} sub="" icon={Clock} color="#DC2626" />
                  <SummaryCard label="Alerts" value={s.alertCount || 0} sub="" icon={Zap} color="#F97316" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {/* Donut */}
                  <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>RAID Distribution</h3>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart><Pie data={DONUT_DATA} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                        {DONUT_DATA.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie><Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} /></PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 mt-2">{DONUT_DATA.map(d => <span key={d.name} className="flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />{d.name}: {d.value}</span>)}</div>
                  </div>

                  {/* Risk Matrix (compact) */}
                  <div className="lg:col-span-2">
                    <RiskMatrix matrixData={matrixData} matrixType={matrixType} onToggle={setMatrixType} onCellClick={(cell) => { setSelectedItem(null); toast.info(`${cell.risks.length} risks at P${cell.probability}×I${cell.impact}`); }} />
                  </div>
                </div>

                {/* Alerts */}
                {s.alerts?.length > 0 && (
                  <div className="rounded-xl p-4" style={{ backgroundColor: '#F5910B08', border: '1px solid #F5910B20' }}>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: '#F59E0B' }}>Alerts ({s.alerts.length})</h3>
                    <div className="space-y-1">{s.alerts.slice(0, 5).map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <AlertTriangle className="w-3 h-3 text-orange-500" />
                        <Badge text={a.type} color="#F97316" />
                        <span style={{ color: 'var(--text-primary)' }}>{a.raidNumber} — {a.title?.slice(0, 50)}</span>
                        <span className="ml-auto" style={{ color: 'var(--text-muted)' }}>{a.owner}</span>
                      </div>
                    ))}</div>
                  </div>
                )}

                {/* Action Aging + Owner workload */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {aging.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Item Aging</h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={aging} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} /><YAxis dataKey="bucket" type="category" width={50} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
                          <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} /></BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {ownerData.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Owner Workload</h3>
                      <DataTable columns={[
                        { key: 'owner', label: 'Owner' },
                        { key: 'total', label: 'Total', render: v => <strong>{v}</strong> },
                        { key: 'risks', label: 'R', render: v => <span style={{ color: '#EF4444' }}>{v}</span> },
                        { key: 'issues', label: 'I', render: v => <span style={{ color: '#3B82F6' }}>{v}</span> },
                        { key: 'critical', label: 'Crit', render: v => v > 0 ? <span className="font-bold text-red-500">{v}</span> : <span style={{ color: 'var(--text-disabled)' }}>0</span> },
                        { key: 'overdue', label: 'Overdue', render: v => v > 0 ? <span className="text-red-500">{v}</span> : '0' },
                      ]} data={ownerData} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CATEGORY TABS */}
            {['risks', 'assumptions', 'issues', 'dependencies'].includes(activeTab) && (
              <div className="space-y-4">
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  <DataTable columns={
                    activeTab === 'risks' ? [
                      { key: 'raidNumber', label: '#', render: v => <span className="font-mono text-xs font-semibold" style={{ color: CAT_COLORS.RISK }}>{v}</span> },
                      { key: 'title', label: 'Title', render: (v) => <span className="text-xs">{v?.slice(0, 60)}</span> },
                      { key: 'riskSpecific', label: 'P×I', render: (v) => { const rs = v || {}; return rs.riskScore ? <span className="font-bold" style={{ color: RATING_COLORS[rs.riskRating] }}>{rs.probabilityScore}×{rs.impactScore}={rs.riskScore}</span> : '—'; } },
                      { key: 'priority', label: 'Rating', render: (v) => <Badge text={v} color={RATING_COLORS[v]} /> },
                      { key: 'riskSpecific_resp', label: 'Response', render: (_, r) => <span className="text-[10px]">{(r.riskSpecific || {}).responseStrategy?.replace(/_/g, ' ') || '—'}</span> },
                      { key: 'ownerName', label: 'Owner' },
                      { key: 'status', label: 'Status', render: v => <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> },
                      { key: 'riskSpecific_emv', label: 'EMV', render: (_, r) => <span className="text-xs">{formatINR((r.riskSpecific || {}).quantitativeAssessment?.expectedMonetaryValue)}</span> },
                      { key: 'identifiedDate', label: 'Age', render: v => { if (!v) return '—'; const d = Math.floor((new Date() - new Date(v)) / 86400000); return <span className="font-mono" style={{ color: d > 60 ? '#DC2626' : d > 30 ? '#F97316' : 'var(--text-primary)' }}>{d}d</span>; } },
                    ] : activeTab === 'assumptions' ? [
                      { key: 'raidNumber', label: '#', render: v => <span className="font-mono text-xs font-semibold" style={{ color: CAT_COLORS.ASSUMPTION }}>{v}</span> },
                      { key: 'title', label: 'Title', render: v => <span className="text-xs">{v?.slice(0, 60)}</span> },
                      { key: 'assumptionSpecific', label: 'Confidence', render: v => { const c = (v || {}).confidenceLevel; return c ? <Badge text={c} color={c === 'HIGH' ? '#22C55E' : c === 'LOW' ? '#EF4444' : '#F59E0B'} /> : '—'; } },
                      { key: 'assumptionSpecific_val', label: 'Validation', render: (_, r) => { const v = (r.assumptionSpecific || {}).validationResult; return v ? <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> : <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>Pending</span>; } },
                      { key: 'ownerName', label: 'Owner' },
                      { key: 'status', label: 'Status', render: v => <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> },
                      { key: 'targetResolutionDate', label: 'Validation Date', render: v => v ? v.slice(0, 10) : '—' },
                    ] : activeTab === 'issues' ? [
                      { key: 'raidNumber', label: '#', render: v => <span className="font-mono text-xs font-semibold" style={{ color: CAT_COLORS.ISSUE }}>{v}</span> },
                      { key: 'title', label: 'Title', render: v => <span className="text-xs">{v?.slice(0, 60)}</span> },
                      { key: 'issueSpecific', label: 'Urgency', render: v => { const u = (v || {}).urgency; return u ? <Badge text={u} color={URGENCY_COLORS[u] || '#94A3B8'} /> : '—'; } },
                      { key: 'priority', label: 'Priority', render: v => <Badge text={v} color={RATING_COLORS[v]} /> },
                      { key: 'issueSpecific_esc', label: 'Esc.Lvl', render: (_, r) => { const l = (r.issueSpecific || {}).escalationLevel; return l > 0 ? <span className="font-bold text-red-500">L{l}</span> : <span style={{ color: 'var(--text-disabled)' }}>—</span>; } },
                      { key: 'ownerName', label: 'Owner' },
                      { key: 'status', label: 'Status', render: v => <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> },
                      { key: 'identifiedDate', label: 'Age', render: v => { if (!v) return '—'; const d = Math.floor((new Date() - new Date(v)) / 86400000); return <span style={{ color: d > 30 ? '#EF4444' : 'var(--text-primary)' }}>{d}d</span>; } },
                    ] : [
                      { key: 'raidNumber', label: '#', render: v => <span className="font-mono text-xs font-semibold" style={{ color: CAT_COLORS.DEPENDENCY }}>{v}</span> },
                      { key: 'title', label: 'Title', render: v => <span className="text-xs">{v?.slice(0, 60)}</span> },
                      { key: 'dependencySpecific', label: 'Type', render: v => { const d = v || {}; return <span className="text-[10px]">{d.dependencyType} / {d.dependencyDirection}</span>; } },
                      { key: 'dependencySpecific_prov', label: 'Provider', render: (_, r) => <span className="text-xs">{(r.dependencySpecific || {}).provider?.partyName || '—'}</span> },
                      { key: 'dependencySpecific_req', label: 'Required', render: (_, r) => { const d = (r.dependencySpecific || {}).requiredDate; return d ? d.slice(0, 10) : '—'; } },
                      { key: 'ownerName', label: 'Owner' },
                      { key: 'status', label: 'Status', render: v => <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> },
                      { key: 'dependencySpecific_delay', label: 'Delay', render: (_, r) => { const d = (r.dependencySpecific || {}).delayDays; return d != null ? <span style={{ color: d > 0 ? '#EF4444' : '#22C55E' }}>{d}d</span> : '—'; } },
                    ]
                  } data={filteredItems(activeTab.toUpperCase().replace('S', '').replace('IE', 'Y').replace('DEPENDENCIE', 'DEPENDENCY').replace('ASSUMPTION', 'ASSUMPTION').replace('RISK', 'RISK').replace('ISSUE', 'ISSUE')
                    === 'RISK' ? 'RISK' : activeTab === 'risks' ? 'RISK' : activeTab === 'assumptions' ? 'ASSUMPTION' : activeTab === 'issues' ? 'ISSUE' : 'DEPENDENCY')}
                    onRowClick={(row) => setSelectedItem(row)} />
                </div>
              </div>
            )}

            {/* RISK MATRIX TAB */}
            {activeTab === 'matrix' && (
              <div className="space-y-5">
                <RiskMatrix matrixData={matrixData} matrixType={matrixType} onToggle={(t) => { setMatrixType(t); }} onCellClick={(cell) => {
                  if (cell.risks?.length > 0) toast.info(`${cell.risks.length} risks: ${cell.risks.map(r => r.raidNumber).join(', ')}`);
                }} />
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Risk Register (sorted by score)</h3>
                  <DataTable columns={[
                    { key: 'raidNumber', label: '#', render: v => <span className="font-mono text-xs font-bold" style={{ color: '#EF4444' }}>{v}</span> },
                    { key: 'title', label: 'Title' },
                    { key: 'riskSpecific', label: 'P', render: v => (v || {}).probabilityScore || '—' },
                    { key: 'riskSpecific_i', label: 'I', render: (_, r) => (r.riskSpecific || {}).impactScore || '—' },
                    { key: 'riskSpecific_s', label: 'Score', render: (_, r) => { const rs = r.riskSpecific || {}; return rs.riskScore ? <span className="font-bold" style={{ color: RATING_COLORS[rs.riskRating] }}>{rs.riskScore}</span> : '—'; } },
                    { key: 'priority', label: 'Rating', render: v => <Badge text={v} color={RATING_COLORS[v]} /> },
                    { key: 'ownerName', label: 'Owner' },
                    { key: 'status', label: 'Status', render: v => <Badge text={v} color={STATUS_COLORS[v] || '#94A3B8'} /> },
                  ]} data={filteredItems('RISK').sort((a, b) => ((b.riskSpecific || {}).riskScore || 0) - ((a.riskSpecific || {}).riskScore || 0))} onRowClick={setSelectedItem} />
                </div>
              </div>
            )}

            {/* ACTION TRACKER TAB */}
            {activeTab === 'actions' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <SummaryCard label="Total Actions" value={allActions.length} icon={CheckCircle2} color="#0EA5E9" />
                  <SummaryCard label="Open" value={allActions.filter(a => a.status === 'OPEN').length} icon={Clock} color="#3B82F6" />
                  <SummaryCard label="In Progress" value={allActions.filter(a => a.status === 'IN_PROGRESS').length} icon={Activity} color="#143A6B" />
                  <SummaryCard label="Overdue" value={allActions.filter(a => a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && a.targetDate && a.targetDate < new Date().toISOString()).length} icon={AlertTriangle} color="#DC2626" />
                  <SummaryCard label="Closure Rate" value={`${allActions.length > 0 ? Math.round(allActions.filter(a => a.status === 'COMPLETED').length / allActions.length * 100) : 0}%`} icon={TrendingUp} color="#22C55E" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  <DataTable columns={[
                    { key: 'parentRaid', label: 'RAID #', render: (v, r) => <span className="font-mono text-xs" style={{ color: CAT_COLORS[r.parentCategory] }}>{v}</span> },
                    { key: 'parentCategory', label: 'Cat', render: v => <Badge text={v} color={CAT_COLORS[v]} /> },
                    { key: 'actionId', label: 'Action ID', render: v => <span className="font-mono text-[10px]">{v}</span> },
                    { key: 'actionDescription', label: 'Description', render: v => <span className="text-xs">{v?.slice(0, 50)}</span> },
                    { key: 'actionType', label: 'Type', render: v => <span className="text-[10px]">{v?.replace(/_/g, ' ')}</span> },
                    { key: 'responsiblePerson', label: 'Owner' },
                    { key: 'targetDate', label: 'Target', render: v => v ? v.slice(0, 10) : '—' },
                    { key: 'status', label: 'Status', render: v => <Badge text={v} color={v === 'COMPLETED' ? '#22C55E' : v === 'IN_PROGRESS' ? '#3B82F6' : '#F59E0B'} /> },
                  ]} data={allActions.sort((a, b) => { const ao = a.status !== 'COMPLETED' && a.targetDate < new Date().toISOString() ? -1 : 0; const bo = b.status !== 'COMPLETED' && b.targetDate < new Date().toISOString() ? -1 : 0; return ao - bo || (a.targetDate || '').localeCompare(b.targetDate || ''); })} />
                </div>
              </div>
            )}


            {/* CROSS-MODULE TAB */}
            {activeTab === 'crossmodule' && (
              <div className="space-y-5" data-testid="cross-module-tab">
                {/* Summary Cards */}
                {crossModuleSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <SummaryCard label="Linked Items" value={crossModuleSummary.totalLinkedItems || 0} icon={Link2} color="#6366F1" />
                    <SummaryCard label="Schedule Links" value={crossModuleSummary.byModule?.SCHEDULE || 0} icon={Calendar} color="#3B82F6" />
                    <SummaryCard label="Cost Links" value={crossModuleSummary.byModule?.COST || 0} icon={DollarSign} color="#F59E0B" />
                    <SummaryCard label="HSE Links" value={crossModuleSummary.byModule?.HSE || 0} icon={Shield} color="#DC2626" />
                    <SummaryCard label="Contingency Allocated" value={formatINR(crossModuleSummary.contingency?.totalAllocated)} icon={Target} color="#143A6B" />
                    <SummaryCard label="Contingency Drawn" value={`${crossModuleSummary.contingency?.utilizationPct || 0}%`} sub={formatINR(crossModuleSummary.contingency?.totalDrawn)} icon={TrendingDown} color={crossModuleSummary.contingency?.utilizationPct > 50 ? '#EF4444' : '#22C55E'} />
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Contingency Tracker */}
                  {contingencyData?.risks?.length > 0 && (
                    <div className="rounded-xl p-5 lg:col-span-2" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Risk Contingency Tracker</h3>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          Allocated: <strong style={{ color: '#143A6B' }}>{formatINR(contingencyData.summary?.totalAllocated)}</strong>
                          {' | '}Drawn: <strong style={{ color: '#EF4444' }}>{formatINR(contingencyData.summary?.totalDrawn)}</strong>
                          {' | '}Remaining: <strong style={{ color: '#22C55E' }}>{formatINR(contingencyData.summary?.remaining)}</strong>
                        </div>
                      </div>
                      <DataTable columns={[
                        { key: 'raidNumber', label: 'RAID #', render: v => <span className="font-mono font-bold text-xs" style={{ color: '#EF4444' }}>{v}</span> },
                        { key: 'title', label: 'Risk', render: v => <span className="text-xs">{v?.slice(0, 40)}</span> },
                        { key: 'priority', label: 'Priority', render: v => <Badge text={v} color={RATING_COLORS[v] || '#94A3B8'} /> },
                        { key: 'emv', label: 'EMV', render: v => <span className="text-xs font-mono">{formatINR(v)}</span> },
                        { key: 'costCodesLinked', label: 'Cost Codes', render: v => <span className="text-[10px] font-mono">{(v || []).join(', ')}</span> },
                        { key: 'contingencyAllocated', label: 'Allocated', render: v => <span className="text-xs font-mono" style={{ color: '#143A6B' }}>{formatINR(v)}</span> },
                        { key: 'contingencyDrawn', label: 'Drawn', render: v => <span className="text-xs font-mono" style={{ color: '#EF4444' }}>{formatINR(v)}</span> },
                        { key: 'contingencyRemaining', label: 'Remaining', render: v => <span className="text-xs font-mono" style={{ color: '#22C55E' }}>{formatINR(v)}</span> },
                        { key: 'utilizationPct', label: '% Used', render: v => (
                          <div className="flex items-center gap-1">
                            <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                              <div className="h-full rounded-full" style={{ width: `${Math.min(v || 0, 100)}%`, backgroundColor: v > 70 ? '#EF4444' : v > 40 ? '#F59E0B' : '#22C55E' }} />
                            </div>
                            <span className="text-[10px] font-mono">{v}%</span>
                          </div>
                        )},
                      ]} data={contingencyData.risks} />
                    </div>
                  )}

                  {/* HSE Links */}
                  {hseLinksData?.hseLinks?.length > 0 && (
                    <div className="rounded-xl p-5 lg:col-span-2" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>HSE Incident Linkages</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>{hseLinksData.totalLinks} links to {hseLinksData.uniqueIncidents} incidents</span>
                      </div>
                      <DataTable columns={[
                        { key: 'raidNumber', label: 'RAID #', render: v => <span className="font-mono font-bold text-xs" style={{ color: CAT_COLORS[v?.startsWith('R') ? 'RISK' : v?.startsWith('I') ? 'ISSUE' : 'DEPENDENCY'] || '#6B7280' }}>{v}</span> },
                        { key: 'category', label: 'Cat', render: v => <Badge text={v} color={CAT_COLORS[v]} /> },
                        { key: 'title', label: 'RAID Title', render: v => <span className="text-xs">{v?.slice(0, 35)}</span> },
                        { key: 'linkType', label: 'Link', render: v => <Badge text={v?.replace(/_/g, ' ')} color="#6366F1" /> },
                        { key: 'incidentNumber', label: 'Incident #', render: v => <span className="font-mono text-xs font-bold" style={{ color: '#DC2626' }}>{v}</span> },
                        { key: 'incidentType', label: 'Type', render: v => <span className="text-xs">{v?.replace(/_/g, ' ')}</span> },
                        { key: 'incidentSeverity', label: 'Severity', render: v => <Badge text={v} color={v === 'FATAL' ? '#DC2626' : v === 'MAJOR' ? '#F97316' : v === 'MODERATE' ? '#F59E0B' : '#94A3B8'} /> },
                        { key: 'incidentStatus', label: 'Status', render: v => <Badge text={v} color={v === 'CLOSED' ? '#22C55E' : '#3B82F6'} /> },
                      ]} data={hseLinksData.hseLinks} />
                    </div>
                  )}

                  {/* All Links Table */}
                  {crossModuleSummary?.links?.length > 0 && (
                    <div className="rounded-xl p-5 lg:col-span-2" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>All Cross-Module Links ({crossModuleSummary.totalLinks})</h3>
                      <DataTable columns={[
                        { key: 'raidNumber', label: 'RAID #', render: v => <span className="font-mono font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{v}</span> },
                        { key: 'category', label: 'Cat', render: v => <Badge text={v} color={CAT_COLORS[v]} /> },
                        { key: 'priority', label: 'Priority', render: v => <Badge text={v} color={RATING_COLORS[v] || '#94A3B8'} /> },
                        { key: 'module', label: 'Module', render: v => <Badge text={v} color={{ SCHEDULE: '#3B82F6', COST: '#F59E0B', HSE: '#DC2626', MANPOWER: '#143A6B' }[v] || '#94A3B8'} /> },
                        { key: 'referenceId', label: 'Reference', render: v => <span className="font-mono text-xs">{v}</span> },
                        { key: 'referenceLabel', label: 'Label', render: v => <span className="text-xs">{v}</span> },
                        { key: 'linkType', label: 'Type', render: v => <Badge text={v?.replace(/_/g, ' ')} color="#6366F1" /> },
                        { key: 'contingencyAmount', label: 'Contingency', render: v => v > 0 ? <span className="text-xs font-mono" style={{ color: '#143A6B' }}>{formatINR(v)}</span> : <span style={{ color: 'var(--text-disabled)' }}>—</span> },
                        { key: 'linkId', label: '', render: (v, row) => (
                          <button onClick={async () => {
                            try {
                              await api.delete(`/v1/projects/${projectId}/raid/items/${row.raidNumber}/cross-link/${v}`);
                              toast.success('Link removed');
                              fetchAll();
                            } catch { toast.error('Failed to remove'); }
                          }} className="text-red-400 hover:text-red-300 text-[10px]" data-testid={`remove-link-${v}`}>Remove</button>
                        )},
                      ]} data={crossModuleSummary.links} />
                    </div>
                  )}

                  {/* Add Link Button */}
                  <div className="lg:col-span-2 flex justify-center">
                    <Button size="sm" onClick={() => setShowLinkModal(true)} data-testid="add-cross-link-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Cross-Module Link
                    </Button>
                  </div>
                </div>

                {/* Empty state */}
                {(!crossModuleSummary || crossModuleSummary.totalLinks === 0) && (
                  <div className="text-center py-16">
                    <Link2 className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No cross-module links yet</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>Link RAID items to Schedule, Cost, or HSE modules</p>
                    <Button size="sm" className="mt-4" onClick={() => setShowLinkModal(true)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Create First Link
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === 'analytics' && (
              <div className="space-y-5">
                {/* Resolution Performance Summary */}
                {resolutionPerf && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <SummaryCard label="Total Resolved" value={resolutionPerf.totalResolved || 0} icon={CheckCircle2} color="#22C55E" />
                    <SummaryCard label="Still Open" value={resolutionPerf.totalOpen || 0} icon={Clock} color="#3B82F6" />
                    <SummaryCard label="Avg Resolution" value={`${resolutionPerf.avgResolutionDays || 0}d`} icon={Activity} color="#143A6B" />
                    <SummaryCard label="On-Time Rate" value={`${resolutionPerf.onTimeRate || 0}%`} icon={TrendingUp} color="#22C55E" />
                    <SummaryCard label="Late Resolutions" value={resolutionPerf.late || 0} icon={AlertTriangle} color="#DC2626" />
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Trend */}
                  {trend.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>RAID Creation Trend</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                          <XAxis dataKey="period" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} /><YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} /><Legend wrapperStyle={{ fontSize: 10 }} />
                          <Bar dataKey="risksCreated" name="Risks" fill="#EF4444" radius={[2, 2, 0, 0]} stackId="a" />
                          <Bar dataKey="assumptionsCreated" name="Assumptions" fill="#F59E0B" radius={[2, 2, 0, 0]} stackId="a" />
                          <Bar dataKey="issuesCreated" name="Issues" fill="#3B82F6" radius={[2, 2, 0, 0]} stackId="a" />
                          <Bar dataKey="dependenciesCreated" name="Dependencies" fill="#143A6B" radius={[2, 2, 0, 0]} stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* EMV Pareto */}
                  {emvPareto.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Risk EMV Pareto (Top 20)</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={emvPareto.slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                          <XAxis type="number" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} tickFormatter={v => formatINR(v)} /><YAxis dataKey="raidNumber" type="category" width={45} tick={{ fontSize: 9, fill: 'var(--text-muted)' }} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} formatter={v => formatINR(v)} />
                          <Bar dataKey="emv" fill="#EF4444" radius={[0, 4, 4, 0]} /></BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* Risk Profile Spider */}
                  {riskProfile.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Risk Profile (Spider)</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <RadarChart data={riskProfile}><PolarGrid stroke="var(--border-color)" />
                          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                          <PolarRadiusAxis tick={{ fontSize: 8, fill: 'var(--text-disabled)' }} domain={[0, 100]} />
                          <Radar name="Risk" dataKey="value" stroke="#EF4444" fill="#EF444430" strokeWidth={2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* Priority Distribution */}
                  {catDist?.byPriority && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Priority Distribution</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart><Pie data={Object.entries(catDist.byPriority).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {Object.keys(catDist.byPriority).map(k => <Cell key={k} fill={RATING_COLORS[k] || '#94A3B8'} />)}
                        </Pie><Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} /></PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* Domain Distribution */}
                  {catDist?.byDomain && Object.keys(catDist.byDomain).length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Domain Distribution</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={Object.entries(catDist.byDomain).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ domain: k.replace(/_/g, ' '), count: v }))}><CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                          <XAxis dataKey="domain" tick={{ fontSize: 8, fill: 'var(--text-muted)' }} angle={-35} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
                          <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} /></BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* Resolution by Category */}
                  {resolutionPerf?.byCategory?.length > 0 && (
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Resolution by Category</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={resolutionPerf.byCategory}><CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                          <XAxis dataKey="category" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} /><YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                          <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} /><Legend wrapperStyle={{ fontSize: 10 }} />
                          <Bar dataKey="onTime" name="On Time" fill="#22C55E" stackId="b" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="late" name="Late" fill="#EF4444" stackId="b" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {/* Cross-Links Network */}
                  {crossLinks?.nodes?.length > 0 && (
                    <div className="rounded-xl p-5 lg:col-span-2" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="cross-links-network">
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Cross-Category Links ({crossLinks.nodes.length} nodes, {crossLinks.edges.length} edges)</h3>
                      <div className="flex flex-wrap gap-2">
                        {crossLinks.nodes.map(n => (
                          <div key={n.id} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px]" style={{ backgroundColor: `${CAT_COLORS[n.category] || '#94A3B8'}12`, border: `1px solid ${CAT_COLORS[n.category] || '#94A3B8'}30`, color: CAT_COLORS[n.category] || '#94A3B8' }}>
                            <Link2 className="w-2.5 h-2.5" />
                            <span className="font-mono font-bold">{n.id}</span>
                            {n.title && <span className="opacity-70 ml-1">{n.title.slice(0, 25)}</span>}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 space-y-1">
                        {crossLinks.edges.slice(0, 15).map((e, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{e.source}</span>
                            <ChevronRight className="w-3 h-3" />
                            <Badge text={e.linkType?.replace(/_/g, ' ')} color="#6366F1" />
                            <ChevronRight className="w-3 h-3" />
                            <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{e.target}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Owner Performance Matrix */}
                  {ownerData.length > 0 && (
                    <div className="rounded-xl p-5 lg:col-span-2" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Owner Performance Matrix</h3>
                      <DataTable columns={[
                        { key: 'owner', label: 'Owner', render: v => <span className="font-medium">{v}</span> },
                        { key: 'total', label: 'Total', render: v => <strong>{v}</strong> },
                        { key: 'risks', label: 'Risks', render: v => <span style={{ color: '#EF4444' }}>{v}</span> },
                        { key: 'assumptions', label: 'Assumptions', render: v => <span style={{ color: '#F59E0B' }}>{v}</span> },
                        { key: 'issues', label: 'Issues', render: v => <span style={{ color: '#3B82F6' }}>{v}</span> },
                        { key: 'dependencies', label: 'Deps', render: v => <span style={{ color: '#143A6B' }}>{v}</span> },
                        { key: 'critical', label: 'Critical', render: v => v > 0 ? <span className="font-bold text-red-500">{v}</span> : <span style={{ color: 'var(--text-disabled)' }}>0</span> },
                        { key: 'actionItems', label: 'Actions', render: v => v > 0 ? <span className="font-medium">{v}</span> : '0' },
                        { key: 'overdue', label: 'Overdue', render: v => v > 0 ? <span className="text-red-500 font-bold">{v}</span> : <span style={{ color: 'var(--text-disabled)' }}>0</span> },
                      ]} data={ownerData} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Form */}
        {showCreate && <CreateRAIDForm projectId={projectId} onCreated={() => { setShowCreate(false); fetchAll(); }} onClose={() => setShowCreate(false)} />}

        {/* Import Modal */}
        {showImport && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 bg-black/40" onClick={() => setShowImport(false)}>
            <div className="w-full max-w-md rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="raid-import-modal">
              <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Import RAID Items</h2>
                <button onClick={() => setShowImport(false)}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)' }}>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Download the template, fill in your data, then upload the file.</p>
                  <Button variant="outline" size="sm" onClick={handleTemplateDownload} data-testid="raid-download-template-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download Template
                  </Button>
                </div>
                <div className="rounded-lg p-6 text-center border-2 border-dashed" style={{ borderColor: 'var(--border-color)' }}>
                  <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                  <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Select an Excel file (.xlsx) with RAID data</p>
                  <input type="file" accept=".xlsx,.xls" id="raid-import-file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleImport(e.target.files[0]); }} data-testid="raid-import-file-input" />
                  <Button size="sm" onClick={() => document.getElementById('raid-import-file').click()} data-testid="raid-import-upload-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Choose File
                  </Button>
                </div>
                <p className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>Sheets: Risks, Assumptions, Issues, Dependencies. Columns: S.No, Title, Domain, Priority, Owner, Description, Area.</p>
              </div>
            </div>
          </div>
        )}

        {/* Cross-Module Link Modal */}
        {showLinkModal && (
          <CrossModuleLinkModal projectId={projectId} items={items} onClose={() => setShowLinkModal(false)} onLinked={() => { setShowLinkModal(false); fetchAll(); }} />
        )}


        {/* Detail Panel */}
        {selectedItem && <ItemDetail item={selectedItem} projectId={projectId} onClose={() => setSelectedItem(null)} onUpdated={() => { setSelectedItem(null); fetchAll(); }} />}

        {/* Empty state */}
        {!loading && projectId && !summary?.risks && (
          <div className="text-center py-16 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>No RAID data. Click "Seed" to generate demo data.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
